import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportAPI, crmAPI } from '../services/api';

const Dashboard = () => {
  const { user, tenant } = useAuth();
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crmStats, setCrmStats] = useState(null);
  const isCrmStaff = user?.role === 'crm_staff';
  const navigate = useNavigate();
  const primary = tenant ? tenant.branding.primary_color || '#1B6B3A' : '#1B6B3A';
  const canCreateTicket = user && !['director','isp_dist_head','area_engineer','subdist_admin'].includes(user.role);

  useEffect(() => {
    if (isCrmStaff) {
      crmAPI.myStats()
        .then(r => setCrmStats(r.data))
        .finally(() => setLoading(false));
    } else {
      Promise.all([reportAPI.dailySummary(), reportAPI.pendingTickets()])
        .then(([s, p]) => { setSummary(s.data); setPending(p.data.tickets.slice(0, 6)); })
        .finally(() => setLoading(false));
    }
  }, [isCrmStaff]);

  const handleDownloadReport = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await reportAPI.dailySummary({ date: today });
      const data = res.data;
      const rows = [
        ['FibraDesk Daily Report - ' + today], [''],
        ['Summary'], ['Opened Today', data.summary.opened],
        ['Closed Today', data.summary.closed], ['Pending', data.summary.pending],
        ['Escalated', data.summary.escalated], ['SLA Breached', data.summary.sla_breached],
        [''], ['By Category'], ['Category', 'Count'],
        ...data.by_category.map(c => [c._id, c.count])
      ];
      const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'FibraDesk_Report_' + today + '.csv';
      a.click();
    } catch { alert('Failed to download report'); }
  };

  const priorityColor = { high: '#ef4444', critical: '#b91c1c', medium: '#f59e0b', low: '#10b981' };
  const priorityBg = { high: '#fef2f2', critical: '#fff1f2', medium: '#fffbeb', low: '#ecfdf5' };
  const statusColor = { open: '#3b82f6', assigned: '#8b5cf6', in_progress: '#f59e0b', escalated: '#ef4444', resolved: '#10b981', closed: '#6b7280' };
  const statusBg = { open: '#eff6ff', assigned: '#f5f3ff', in_progress: '#fffbeb', escalated: '#fef2f2', resolved: '#ecfdf5', closed: '#f9fafb' };

  const StatCard = ({ title, value, icon, color, bg, sub, onClick }) => (
    <div onClick={onClick} style={{
      background: 'white', borderRadius: '16px', padding: '24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9',
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s',
      position: 'relative', overflow: 'hidden'
    }}
      onMouseEnter={e => { if(onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.1)'; }}}
      onMouseLeave={e => { if(onClick) { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.06)'; }}}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: '80px', height: '80px', background: bg, borderRadius: '0 16px 0 80px', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '12px' }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>{title}</p>
      <h2 style={{ margin: '0 0 4px', fontSize: '36px', fontWeight: '800', color: color, lineHeight: 1 }}>{value ?? '—'}</h2>
      {sub && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#94a3b8' }}>{sub}</p>}
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', flexDirection: 'column', gap: '16px' }}>
      <div style={{ width: '40px', height: '40px', border: `3px solid ${primary}22`, borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#94a3b8', fontSize: '14px' }}>Loading dashboard...</p>
    </div>
  );

  // CRM Staff Dashboard
  if (isCrmStaff && crmStats) {
    const outcomeLabels = { reactivated:'✅ Reactivated', contacted:'📞 Contacted', not_reachable:'📵 Not Reachable', busy:'⏰ Busy', wrong_number:'❌ Wrong Number', removed:'🗑️ Removed', not_applicable:'➖ N/A' };
    const callTypeLabels = { retention:'🔄 Retention', installation:'🔧 Installation', support:'🛠️ Support', followup:'📋 Follow-up', other:'📌 Other' };
    const outcomes = Object.fromEntries((crmStats.byOutcome||[]).map(o=>([o._id, o.count])));
    const callTypes = Object.fromEntries((crmStats.byCallType||[]).map(o=>([o._id, o.count])));
    return (
      <div style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)`, borderRadius: '20px', padding: '28px 32px', marginBottom: '24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
          <h2 style={{ color: '#fff', margin: 0, fontSize: '22px', fontWeight: 700 }}>👋 Welcome, {user?.name?.split(' ')[0]}!</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: '4px 0 0', fontSize: '14px' }}>CRM Dashboard — {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}</p>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Today', value: crmStats.today, icon: '📅', color: primary, bg: primary+'15' },
            { label: 'This Week', value: crmStats.thisWeek, icon: '📊', color: '#8b5cf6', bg: '#f5f3ff' },
            { label: 'This Month', value: crmStats.thisMonth, icon: '📆', color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Total Calls', value: crmStats.total, icon: '📞', color: '#10b981', bg: '#ecfdf5' },
          ].map((s,i) => (
            <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>{s.label}</div>
                  <div style={{ fontSize: '32px', fontWeight: 800, color: s.color }}>{s.value}</div>
                </div>
                <div style={{ width: '48px', height: '48px', background: s.bg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>{s.icon}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          {/* Outcomes */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>📊 Call Outcomes</h3>
            {Object.entries(outcomeLabels).map(([key, label]) => outcomes[key] ? (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#475569' }}>{label}</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: primary, background: primary+'15', padding: '2px 10px', borderRadius: '12px' }}>{outcomes[key]}</span>
              </div>
            ) : null)}
          </div>

          {/* Call Types */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>📋 Call Types</h3>
            {Object.entries(callTypeLabels).map(([key, label]) => callTypes[key] ? (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#475569' }}>{label}</span>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#8b5cf6', background: '#f5f3ff', padding: '2px 10px', borderRadius: '12px' }}>{callTypes[key]}</span>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Follow-ups Today */}
        {crmStats.followUps?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>⏰ Pending Follow-ups</h3>
            {crmStats.followUps.map((f,i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < crmStats.followUps.length-1 ? '1px solid #f1f5f9' : 'none' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>{f.customer_name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{f.remarks || 'No remarks'}</div>
                </div>
                <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 600, background: '#fffbeb', padding: '4px 10px', borderRadius: '8px' }}>{new Date(f.follow_up_date).toLocaleDateString('en-IN')}</div>
              </div>
            ))}
          </div>
        )}

        {/* Recent Calls */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>🕐 Recent Calls</h3>
          {(crmStats.recent||[]).map((r,i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: i < crmStats.recent.length-1 ? '1px solid #f1f5f9' : 'none' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>{r.customer_name}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{outcomeLabels[r.outcome]||r.outcome}</div>
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>{new Date(r.createdAt).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Welcome Banner */}
      <div style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)`, borderRadius: '20px', padding: '28px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', right: '-20px', top: '-20px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', right: '80px', bottom: '-40px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: '800', color: 'white', letterSpacing: '-0.3px' }}>
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '14px' }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {summary && summary.summary?.pending > 0 && ` · ${summary.summary.pending} ticket${summary.summary.pending > 1 ? 's' : ''} awaiting attention`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', position: 'relative', zIndex: 1 }}>
          <button onClick={handleDownloadReport} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'white', backdropFilter: 'blur(10px)' }}>
            📥 Export
          </button>
          {canCreateTicket && (
            <button onClick={() => navigate('/tickets/new')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: primary }}>
              ➕ New Ticket
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard title="Opened Today" value={summary.summary?.opened} icon="🎫" color={primary} bg={`${primary}10`} sub="New tickets today" onClick={() => { const d = new Date(); const ist = new Date(d.getTime() + (5.5*60*60*1000)); const today = ist.toISOString().split('T')[0]; navigate('/tickets?date_from=' + today + '&date_to=' + today); }} />
          <StatCard title="Pending" value={summary.summary?.pending} icon="⏳" color="#f59e0b" bg="#fffbeb" sub="Awaiting resolution" onClick={() => navigate('/tickets?status=open%2Cassigned%2Cin_progress%2Cescalated')} />
          <StatCard title="Escalated" value={summary.summary?.escalated} icon="🚨" color="#ef4444" bg="#fef2f2" sub="Needs attention" onClick={() => navigate('/tickets?status=escalated')} />
          <StatCard title="Resolved Today" value={(summary.summary?.resolved||0)+(summary.summary?.closed||0)} icon="✅" color="#10b981" bg="#ecfdf5" sub="Successfully closed" onClick={() => { const d = new Date(); const ist = new Date(d.getTime() + (5.5*60*60*1000)); const today = ist.toISOString().split('T')[0]; navigate('/tickets?status=resolved,closed&date_from=' + today + '&date_to=' + today); }} />
        </div>
      )}

      {/* Second row */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <StatCard title="SLA Breached" value={summary.summary?.sla_breached} icon="⚠️" color="#ef4444" bg="#fef2f2" sub="Action required" onClick={() => navigate('/tickets?sla_breached=true')} />
          <StatCard title="Avg Resolution" value={summary.summary?.avg_resolution_hours ? summary.summary.avg_resolution_hours.toFixed(1) + 'h' : 'N/A'} icon="⏱️" color="#8b5cf6" bg="#f5f3ff" sub="Average time to resolve" onClick={() => navigate('/tickets?status=resolved,closed')} />
          <StatCard title="Total Active" value={summary.summary?.pending || 0} icon="📊" color="#3b82f6" bg="#eff6ff" sub="All open tickets" onClick={() => navigate('/tickets?status=open%2Cassigned%2Cin_progress%2Cescalated')} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Pending Tickets */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>⏳ Pending Tickets</h3>
            <button onClick={() => navigate('/tickets')} style={{ background: `${primary}10`, border: 'none', color: primary, padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>View All →</button>
          </div>
          {pending.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
              <div style={{ fontSize: '14px' }}>No pending tickets!</div>
            </div>
          ) : pending.map(t => (
            <div key={t._id} onClick={() => navigate('/tickets/' + t._id)} style={{
              padding: '12px', borderRadius: '10px', marginBottom: '8px',
              border: '1px solid #f1f5f9', cursor: 'pointer', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = '#f1f5f9'; }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: priorityBg[t.priority] || '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '16px' }}>{t.priority === 'critical' ? '🔴' : t.priority === 'high' ? '🟠' : t.priority === 'medium' ? '🟡' : '🟢'}</span>
              </div>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.customer_name}</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.issue_description?.substring(0, 50)}...</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                <span style={{ background: statusBg[t.status] || '#f8fafc', color: statusColor[t.status] || '#64748b', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' }}>{t.status?.replace(/_/g, ' ')}</span>
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>#{t.ticket_number}</span>
              </div>
            </div>
          ))}
        </div>

        {/* By Category */}
        <div style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>📊 Tickets by Category</h3>
          {summary?.by_category?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              <div style={{ fontSize: '14px' }}>No data available</div>
            </div>
          ) : summary?.by_category?.map((cat, i) => {
            const max = Math.max(...(summary?.by_category?.map(c => c.count) || [1]));
            const pct = Math.round((cat.count / max) * 100);
            const colors = [primary, '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];
            return (
              <div key={i} style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', textTransform: 'capitalize' }}>{cat._id || 'Unknown'}</span>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: colors[i % colors.length] }}>{cat.count}</span>
                </div>
                <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct + '%', background: colors[i % colors.length], borderRadius: '3px', transition: 'width 0.6s ease' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
